import { useEffect, useState, type JSX } from 'react';
import { Link } from 'react-router-dom';
import { visibleAudiences, canWrite } from '@cca/corpus-shared';
import { api, type DocDocument } from '../api/client';
import { useAppContext } from '../frame/AppShell';

/** The document list — the landing surface after sign-in. */
export function DocumentListPage(): JSX.Element {
  const { session } = useAppContext();
  const [documents, setDocuments] = useState<DocDocument[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  // The row currently being duplicated (showing the with/without-rationale choice).
  const [dupId, setDupId] = useState<string | null>(null);

  const reload = () =>
    api
      .documents()
      .then((r) => setDocuments(r.documents))
      .catch((e: unknown) => setError(e instanceof Error ? e.message : 'Unknown error'));

  const onDuplicate = async (doc: DocDocument, includeCommentary: boolean) => {
    setBusyId(doc.id);
    setError(null);
    try {
      await api.duplicateDocument(doc.id, includeCommentary);
      setDupId(null);
      await reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not duplicate the document.');
    } finally {
      setBusyId(null);
    }
  };

  const onRename = async (doc: DocDocument) => {
    const next = window.prompt('Rename this document', doc.title);
    if (next === null) return; // cancelled
    const title = next.trim();
    if (!title || title === doc.title) return;
    setBusyId(doc.id);
    setError(null);
    try {
      await api.renameDocument(doc.id, title);
      setDocuments((ds) => (ds ?? []).map((d) => (d.id === doc.id ? { ...d, title } : d)));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not rename the document.');
    } finally {
      setBusyId(null);
    }
  };

  const onDelete = async (doc: DocDocument) => {
    if (
      !window.confirm(
        `Permanently delete “${doc.title}” and all its blocks and notes? This cannot be undone.`,
      )
    )
      return;
    setBusyId(doc.id);
    setError(null);
    try {
      await api.deleteDocument(doc.id);
      setDocuments((ds) => (ds ?? []).filter((d) => d.id !== doc.id));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not delete the document.');
    } finally {
      setBusyId(null);
    }
  };

  useEffect(() => {
    let alive = true;
    api
      .documents()
      .then((r) => alive && setDocuments(r.documents))
      .catch((e: unknown) => alive && setError(e instanceof Error ? e.message : 'Unknown error'));
    return () => {
      alive = false;
    };
  }, []);

  return (
    <div className="stack" style={{ maxWidth: 'var(--maxw-reading)' }}>
      <div>
        <h1
          style={{
            margin: '0 0 var(--space-2)',
            fontSize: 'var(--text-xl)',
            color: 'var(--color-structural)',
          }}
        >
          Documents
        </h1>
        <p className="muted" style={{ margin: 0, fontSize: 'var(--text-sm)' }}>
          Signed in as <strong>{session.email}</strong> · you may read{' '}
          <strong>{visibleAudiences(session.tier).join(', ')}</strong> commentary
          {canWrite(session.tier) ? ' · editing enabled' : ''}.
        </p>
        {canWrite(session.tier) && (
          <Link to="/import" className="btn btn-ghost" style={{ marginTop: 'var(--space-3)' }}>
            Import a service
          </Link>
        )}
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      {!documents && !error && <p className="muted">Loading…</p>}

      {documents && documents.length === 0 && (
        <div className="card">
          <p style={{ margin: 0 }}>No documents yet.</p>
        </div>
      )}

      {documents && documents.length > 0 && (
        <ul className="doc-list">
          {documents.map((d) => (
            <li key={d.id} className="doc-list-row">
              <Link to={`/read/${encodeURIComponent(d.id)}`} className="doc-list-main">
                <span className="doc-list-title">{d.title}</span>
                {d.subtitle && <span className="doc-list-sub muted">{d.subtitle}</span>}
              </Link>
              <div className="doc-list-foot">
                <span className="doc-list-meta muted">
                  {d.status} · updated{' '}
                  {new Date(d.updatedAt).toLocaleDateString('en-NZ', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                  })}
                </span>
                {canWrite(session.tier) &&
                  (dupId === d.id ? (
                    <span className="doc-row-actions" role="group" aria-label="Copy options">
                      <span className="muted" style={{ fontSize: 'var(--text-xs)' }}>
                        Copy with…
                      </span>
                      <button
                        type="button"
                        className="doc-act"
                        onClick={() => onDuplicate(d, true)}
                        disabled={busyId === d.id}
                        title="Copy including the rationale sticky notes"
                      >
                        Rationale notes
                      </button>
                      <button
                        type="button"
                        className="doc-act"
                        onClick={() => onDuplicate(d, false)}
                        disabled={busyId === d.id}
                        title="Copy the service only — no rationale notes"
                      >
                        Service only
                      </button>
                      <button
                        type="button"
                        className="doc-del"
                        onClick={() => setDupId(null)}
                        disabled={busyId === d.id}
                      >
                        Cancel
                      </button>
                    </span>
                  ) : (
                    <span className="doc-row-actions">
                      <button
                        type="button"
                        className="doc-act"
                        onClick={() => {
                          setError(null);
                          setDupId(d.id);
                        }}
                        disabled={busyId !== null}
                        title="Copy this document"
                      >
                        {busyId === d.id ? 'Copying…' : 'Copy'}
                      </button>
                      <button
                        type="button"
                        className="doc-act"
                        onClick={() => onRename(d)}
                        disabled={busyId !== null}
                        title="Rename this document"
                      >
                        Rename
                      </button>
                      <button
                        type="button"
                        className="doc-del"
                        onClick={() => onDelete(d)}
                        disabled={busyId === d.id}
                        title="Delete this document"
                      >
                        {busyId === d.id ? 'Deleting…' : 'Delete'}
                      </button>
                    </span>
                  ))}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
